import * as RadixSelect from '@radix-ui/react-select';
import { CaretDown, Check } from '@phosphor-icons/react';
import {
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type TextareaHTMLAttributes,
} from 'react';
import { cn } from '@/lib/cn';

const CONTROL =
  'w-full rounded-[var(--radius-control)] border border-border bg-surface px-3 py-2 ' +
  'text-[0.9375rem] text-text placeholder:text-text-muted/70 ' +
  'transition-[border-color,box-shadow] duration-200 ease-[var(--ease-gentle)] ' +
  'hover:border-border-strong focus:border-accent focus:outline-none ' +
  'disabled:opacity-50';

interface FieldShellProps {
  label: string;
  /** 錯誤文字固定在輸入欄下方（spec 25.1）。 */
  error?: string | null;
  hint?: string;
  children: (props: { id: string; describedBy: string | undefined }) => ReactNode;
}

/** 表單標籤固定在輸入欄上方，錯誤文字在下方。 */
export function Field({ label, error, hint, children }: FieldShellProps) {
  const id = useId();
  const messageId = `${id}-message`;
  const message = error ?? hint;

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-[0.8125rem] font-medium text-text-muted">
        {label}
      </label>
      {children({ id, describedBy: message ? messageId : undefined })}
      {message && (
        <p
          id={messageId}
          className={cn('text-[0.75rem]', error ? 'text-danger' : 'text-text-muted')}
        >
          {message}
        </p>
      )}
    </div>
  );
}

export function TextInput({ className, ...rest }: InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cn(CONTROL, 'h-10', className)} {...rest} />;
}

export function TextArea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <textarea className={cn(CONTROL, 'min-h-24 resize-y leading-relaxed', className)} {...rest} />
  );
}

export interface SelectOption {
  value: string;
  label: string;
}

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  options: readonly SelectOption[];
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
}

export function Select({
  value,
  onValueChange,
  options,
  placeholder = '請選擇',
  className,
  ...rest
}: SelectProps) {
  return (
    <RadixSelect.Root value={value} onValueChange={onValueChange}>
      <RadixSelect.Trigger
        {...rest}
        className={cn(CONTROL, 'flex h-10 items-center justify-between gap-2 text-left', className)}
      >
        <RadixSelect.Value placeholder={placeholder} />
        <RadixSelect.Icon>
          <CaretDown size={14} className="text-text-muted" />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          position="popper"
          sideOffset={6}
          className={cn(
            'z-50 max-h-72 min-w-[var(--radix-select-trigger-width)] overflow-hidden',
            'rounded-[var(--radius-control-lg)] border border-border bg-surface-raised',
            'p-1 shadow-[var(--shadow-raised)]',
          )}
        >
          <RadixSelect.Viewport>
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                className={cn(
                  'flex cursor-default items-center gap-2 rounded-[var(--radius-control)]',
                  'px-2.5 py-2 text-[0.875rem] outline-none select-none',
                  'data-[highlighted]:bg-accent-soft data-[highlighted]:text-text',
                )}
              >
                <span className="flex size-4 items-center justify-center">
                  <RadixSelect.ItemIndicator>
                    <Check size={13} weight="bold" className="text-accent" />
                  </RadixSelect.ItemIndicator>
                </span>
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
